/** @jsxImportSource theme-ui */
import { Box, Text, merge, Button as ThemeUiButton } from "theme-ui";

import { SvgOrImg } from "components";
import { useTiltEffect } from "hooks";
import * as styles from "./ProductButton.styles";
import { getHrefWithHostname } from "utils";

export default function ProductButton({
  logo,
  description,
  productLogo,
  productLogoOverlay,
  subText,
  textColor,
  boxColor,
  borderTop,
  selectLabelHeight,
  selectLabelFontSizeDesktop,
  selectLabelFontSizeMobile,
  selectLabelMarginLeft,
  selectLabelTop,
  selectLabelBorderRadius,
  selectLabelWidth,
  selectLabelBorder,
  bauerLogo,
  selectLabel = "SELECT",
  floatingLabel,
  color,
  backgroundColor,
  backgroundImage,
  isSelected = false,
  isDisabled = false,
  productButtonLinkOverride,
  onClick,
  ...props
}) {
  const { ref, wrapperStyles } = useTiltEffect({ disable: isSelected });

  const onClickHandler = !productButtonLinkOverride ? onClick : null;

  const ctaProps = {
    "aria-label": logo.alt,
    variant: "unstyled",
    "data-attr-track": true,
    "data-attr-analytics-trigger": "product_selection",
    "data-attr-analytics-value": logo.alt,
    onClick: onClickHandler,
    disabled: isDisabled,
    sx: styles.cardButton({
      backgroundImage,
      backgroundColor,
      color,
    }),
    ref,
    ...props,
  };

  const ProductButtonChildren = () => {
    return (
      <>
        <Box sx={styles.labelBox}>
          {description && (
            <Text variant="h5" sx={styles.description}>
              {description}
            </Text>
          )}

          <SvgOrImg
            type={logo?.type}
            src={logo.url}
            alt={logo.alt}
            sx={styles.logo}
          />

          {subText && <Text sx={styles.subText}>{subText}</Text>}

          <Box sx={styles.productLogo}>
            {productLogo?.url && (
              <SvgOrImg
                type={productLogo?.type}
                src={productLogo.url}
                alt={productLogo.alt}
                sx={productLogo?.styles || {}}
              />
            )}
          </Box>

          {productLogoOverlay?.url && (
            <SvgOrImg
              type={productLogoOverlay?.type}
              src={productLogoOverlay.url}
              alt={productLogoOverlay.alt}
              sx={styles.productLogoOverlay}
            />
          )}
        </Box>

        {!isSelected && (
          <Box
            sx={styles.selectLabel({
              boxColor,
              textColor,
              borderTop,
              selectLabelHeight,
              selectLabelFontSizeDesktop,
              selectLabelFontSizeMobile,
              selectLabelMarginLeft,
              selectLabelTop,
              selectLabelBorderRadius,
              selectLabelWidth,
              selectLabelBorder,
            })}
          >
            {bauerLogo?.url && (
              <SvgOrImg
                type={bauerLogo?.type}
                src={bauerLogo?.url ?? ""}
                alt={bauerLogo?.alt ?? ""}
                sx={bauerLogo?.url && styles.bauerLogo}
              />
            )}
            {selectLabel}
          </Box>
        )}

        {floatingLabel && (
          <Box
            sx={styles.floatingLabel({
              styles: floatingLabel.styles,
            })}
          >
            {floatingLabel.text}
          </Box>
        )}
      </>
    );
  };

  if (productButtonLinkOverride) {
    ctaProps.href = getHrefWithHostname(productButtonLinkOverride);
    ctaProps.target = "_blank";
    ctaProps.rel = "noopener noreferrer";
    return (
      <Box
        sx={merge(wrapperStyles, {
          minWidth: "270px",
        })}
      >
        <a {...ctaProps}>
          <ProductButtonChildren />
        </a>
      </Box>
    );
  }

  return (
    <Box
      sx={merge(wrapperStyles, {
        minWidth: "270px",
        display: "flex",
        flexDirection: "row",
        textDecoration: "none",
      })}
    >
      <ThemeUiButton {...ctaProps}>
        <ProductButtonChildren />
      </ThemeUiButton>
    </Box>
  );
}
